import { RouteRecordRaw } from 'vue-router';

import { ROUTE_NAMES } from '@/constants';
import { RouteTab } from '@/models/routing';
import i18n, { loadTranslations } from '@/plugins/i18n';

const CustomMenuOverview = () => import('@/pages/Inventory/CustomMenu/CustomMenuOverview.vue');
const CustomMenuAddEdit = () => import('@/pages/Inventory/CustomMenu/CustomMenuAddEdit.vue');
const CustomMenuAddEditNew = () => import('@/pages/Inventory/CustomMenu/CustomMenuAddEditNew.vue');
const PricingBase = () => import('@/pages/Pricing/index.vue');
const VolumeDiscounts = () => import('@/pages/Pricing/VolumeDiscounts/VolumeDiscounts.vue');
const PromoCodesView = () => import('@/pages/Inventory/Promotions/PromoCodes.vue');
const VolumeDiscountEditCreate = () => import('@/pages/Pricing/VolumeDiscounts/VolumeDiscountEditCreate.vue');

async function beforeEnter() {
  await loadTranslations('pricing');
}

export function tabRoutes(): Readonly<RouteTab[]> {
  return Object.freeze([
    {
      to: { name: ROUTE_NAMES.VOLUME_DISCOUNTS },
      label: i18n.t('pricing.tabs.volumeDiscounts'),
    },
    {
      to: { name: ROUTE_NAMES.PROMO_CODES },
      label: i18n.t('pricing.tabs.promoCodes'),
    },
    {
      to: { name: ROUTE_NAMES.PRICING_CUSTOM_MENUS },
      label: i18n.t('pricing.tabs.customMenus'),
    },
  ]);
}

const pricingRoutes: RouteRecordRaw[] = [
  {
    path: 'pricing',
    component: PricingBase,
    beforeEnter,
    meta: {
      tabs: tabRoutes,
    },
    children: [
      // Volume Discounts
      {
        path: 'volume-discounts',
        name: ROUTE_NAMES.VOLUME_DISCOUNTS,
        beforeEnter: async function (_to, _from, next) {
          await Promise.all([loadTranslations('pricing'), loadTranslations('inventory')]);
          next();
        },
        meta: {
          title: () => i18n.t('pricing.title'),
          description: () => i18n.t('pricing.description'),
        },
        component: VolumeDiscounts,
        children: [
          {
            path: 'create',
            name: ROUTE_NAMES.CREATE_VOLUME_DISCOUNT,
            component: VolumeDiscountEditCreate,
            props: true,
            meta: {
              title: () => i18n.t('pricing.title'),
              description: () => i18n.t('pricing.description'),
              includeNav: true,
            },
          },
          {
            path: ':volumeDiscountId/edit',
            name: ROUTE_NAMES.EDIT_VOLUME_DISCOUNT,
            component: VolumeDiscountEditCreate,
            props: true,
            meta: {
              title: () => i18n.t('pricing.title'),
              description: () => i18n.t('pricing.description'),
              includeNav: true,
            },
          },
        ],
      },
    ],
  },
  {
    path: 'pricing/promo-codes',
    name: ROUTE_NAMES.PROMO_CODES,
    beforeEnter: async function (_to, _from, next) {
      await Promise.all([loadTranslations('pricing'), loadTranslations('inventory')]);
      next();
    },
    component: PromoCodesView,
    meta: {
      tabs: tabRoutes,
      title: () => i18n.t('inventory.promo_codes.title'),
      description: () => i18n.t('inventory.promo_codes.description'),
      permissions: [
        {
          rules: [
            'canEditPrices',
            ({ userActiveCompany, user }) =>
              Boolean(userActiveCompany?.staffId && userActiveCompany.seller) &&
              (user.featureFlags.enableStaffLimitedAccess ? !userActiveCompany?.staffHasLimitedAccess : true),
          ],
        },
      ],
    },
  },
  {
    path: 'pricing/custom-menus',
    name: ROUTE_NAMES.PRICING_CUSTOM_MENUS,
    beforeEnter: async function (_to, _from, next) {
      await Promise.all([loadTranslations('pricing'), loadTranslations('inventory'), loadTranslations('shop')]);
      next();
    },
    component: CustomMenuOverview,
    meta: {
      tabs: tabRoutes,
      title: () => i18n.t('inventory.custom_menus.title'),
      description: () => i18n.t('inventory.custom_menus.description'),
      permissions: [
        {
          rules: [
            'canManageCrm',
            ({ userActiveCompany, user }) =>
              Boolean(userActiveCompany?.staffId && userActiveCompany.seller) &&
              (user.featureFlags.enableStaffLimitedAccess ? !userActiveCompany?.staffHasLimitedAccess : true),
          ],
        },
      ],
    },
  },
  {
    path: 'pricing/custom-menus/add',
    name: ROUTE_NAMES.PRICING_CUSTOM_MENUS_ADD,
    beforeEnter: async function (_to, _from, next) {
      await Promise.all([loadTranslations('pricing'), loadTranslations('inventory')]);
      next();
    },
    component: CustomMenuAddEdit,
    props: true,
    meta: {
      title: () => i18n.t('inventory.custom_menus.titleAdd'),
      permissions: [
        {
          rules: [
            'canManageCrm',
            ({ userActiveCompany, user }) =>
              Boolean(userActiveCompany?.staffId && userActiveCompany.seller) &&
              (user.featureFlags.enableStaffLimitedAccess ? !userActiveCompany?.staffHasLimitedAccess : true),
          ],
        },
      ],
    },
  },
  {
    path: 'pricing/custom-menus/add-new',
    name: ROUTE_NAMES.PRICING_CUSTOM_MENUS_ADD_NEW,
    beforeEnter: async function (_to, _from, next) {
      await Promise.all([loadTranslations('pricing'), loadTranslations('inventory')]);
      next();
    },
    component: CustomMenuAddEditNew,
    meta: {
      title: () => i18n.t('inventory.custom_menus.titleAdd'),
      permissions: [
        {
          rules: [
            'canManageCrm',
            ({ userActiveCompany, user }) =>
              Boolean(userActiveCompany?.staffId && userActiveCompany.seller) &&
              (user.featureFlags.enableStaffLimitedAccess ? !userActiveCompany?.staffHasLimitedAccess : true),
          ],
        },
      ],
    },
  },
  {
    path: 'pricing/custom-menus/:scheduleId/edit',
    name: ROUTE_NAMES.PRICING_CUSTOM_MENUS_EDIT,
    beforeEnter: async function (_to, _from, next) {
      await Promise.all([loadTranslations('pricing'), loadTranslations('inventory')]);
      next();
    },
    props: true,
    component: CustomMenuAddEdit,
    meta: {
      title: () => i18n.t('inventory.custom_menus.titleEdit'),
      permissions: [
        {
          rules: [
            'canManageCrm',
            ({ userActiveCompany, user }) =>
              Boolean(userActiveCompany?.staffId && userActiveCompany.seller) &&
              (user.featureFlags.enableStaffLimitedAccess ? !userActiveCompany?.staffHasLimitedAccess : true),
          ],
        },
      ],
    },
  },
  {
    path: 'pricing/custom-menus/:scheduleId/edit-new',
    name: ROUTE_NAMES.PRICING_CUSTOM_MENUS_EDIT_NEW,
    beforeEnter: async function (_to, _from, next) {
      await Promise.all([loadTranslations('pricing'), loadTranslations('inventory')]);
      next();
    },
    component: CustomMenuAddEditNew,
    meta: {
      title: () => i18n.t('inventory.custom_menus.titleEdit'),
      permissions: [
        {
          rules: [
            'canManageCrm',
            ({ userActiveCompany, user }) =>
              Boolean(userActiveCompany?.staffId && userActiveCompany.seller) &&
              (user.featureFlags.enableStaffLimitedAccess ? !userActiveCompany?.staffHasLimitedAccess : true),
          ],
        },
      ],
    },
  },
];

export default pricingRoutes;
